/* eslint-disable max-lines */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { Keys, locale } from 'src/i18n';
import {
  getCurrentEntityId,
  getEntityListParams,
} from 'src/models/details/selectors';
import {
  clearDetails,
  setCurrentEntityId,
  setDetails,
  setDetailsActivitiesData,
} from 'src/models/details/slice';
import { getFiltersByContext } from 'src/models/filters/selectors';
import { getRevyreWorkflowConfig } from 'src/models/lmsConfig/queries';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { removeFirstPendingActivity } from 'src/models/lmsConfig/slice';
import { REVYRE_FORM } from 'src/modules/constants';
import { getState } from 'src/store';
import axios from 'src/workspace/axios';
import { removeDialog, renderModule } from 'src/workspace/slice';
import { closePortal } from 'src/workspace/utils';
// eslint-disable-next-line vymo-ui/restrict-import
import { fetchEntityDetails, fetchTrails } from '../details360/thunk';
import { REVYRE } from './constants';
import { CASE } from './details/constants';
import { getWorkflowCode } from './form/queries';
import { RevyreFormProps } from './form/types';
import {
  getCurrentWorkflowType,
  getDomainConfigByType,
  getEntityListMetaData,
} from './selectors';
import {
  resetEntityListData,
  setCurrentBulkDownloadJobId,
  setCurrentWorkflowType,
  setDomainType,
  setEntityListData,
  setError,
  setLoading,
  setModuleConfigData,
  setNotification,
} from './slice';
import { BulkDownloadJobPayload, DomainType, WorkflowType } from './types';

const isRevyreMock = localStorage.getItem('isRevyreMock') === 'true';
const url = isRevyreMock
  ? 'https://a556d0d3-4027-4273-ae26-c6bfb566a903.mock.pstmn.io'
  : '';

export const BASE_URL = `${url}/vymo/v3/workflow`;

export const getRequestHeaders = () => ({
  'x-vymo-device-type': 'web',
});

export const initiatePhoneCall = (payload) => async (dispatch) => {
  try {
    await axios.post(`${BASE_URL}/phonecall`, payload, {
      headers: getRequestHeaders(),
    });

    return true;
  } catch (err: any) {
    const details = err?.response?.data?.details;
    const description = details?.description;
    const subErrorCode = details?.subErrorCode;

    if (
      REVYRE.COLLECT_IQ.STATUS_ERROR_CODE_WITH_DESCRIPTION.includes(
        subErrorCode,
      )
    ) {
      dispatch(setError({ message: description, id: String(Date.now()) }));
    } else {
      dispatch(setError({ message: locale(Keys.SOMETHING_WENT_WRONG) }));
    }

    return false;
  }
};

const createWorkflow = (workflowType, payload) =>
  axios.post(`${BASE_URL}/${workflowType}`, payload, {
    headers: getRequestHeaders(),
  });

const createDomain = (domainType, payload) =>
  axios.post(`${BASE_URL}/${domainType}`, payload, {
    headers: getRequestHeaders(),
  });

const updateWorkflow = (workflowType, entityId, eventId, payload) =>
  axios.patch(`${BASE_URL}/${workflowType}/${entityId}/${eventId}`, payload, {
    headers: getRequestHeaders(),
  });

const updateDomain = (domainType, entityId, eventId, payload) =>
  axios.patch(`${BASE_URL}/${domainType}/${entityId}/${eventId}`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchModuleConfig = createAsyncThunk(
  'moduleConfig',
  async (
    {
      domainType,
      workflowType,
      refresh = false,
    }: {
      domainType: DomainType;
      workflowType?: WorkflowType;
      refresh?: boolean;
    },
    { dispatch },
  ) => {
    workflowType = workflowType ?? getCurrentWorkflowType(getState());

    const moduleConfig = getDomainConfigByType(
      getState(),
      domainType,
      workflowType,
    );

    dispatch(setError({ message: '' }));

    if (moduleConfig && !refresh) {
      return;
    }

    try {
      const moduleConfigApi = `${url}/vymo/v3/workflow/${
        domainType === 'workflow' ? workflowType : domainType
      }/config`;
      dispatch(setLoading({ loading: true }));
      const response = await axios.get(moduleConfigApi, {
        headers: getRequestHeaders(),
      });

      const configData = response?.data?.payload?.flows?.[0];
      dispatch(
        setModuleConfigData({
          domainType,
          workflowType,
          data: configData,
        }),
      );
    } catch (error) {
      dispatch(setError({ message: locale(Keys.ERROR_FETCHING_CONFIG_DATA) }));
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const fetchEntityList = createAsyncThunk(
  'entityList',
  async (
    { domainType, payload }: { domainType: DomainType; payload },
    { dispatch },
  ) => {
    try {
      const workflowType = getCurrentWorkflowType(getState());
      const entityListApi = `${BASE_URL}/${
        domainType !== 'workflow' ? domainType : workflowType
      }/list`;
      dispatch(setLoading({ loading: true }));
      dispatch(setError({ message: '' }));
      const response = await axios.post(entityListApi, payload, {
        headers: getRequestHeaders(),
      });
      dispatch(setEntityListData({ data: response?.data }));
    } catch (error) {
      dispatch(setError({ message: locale(Keys.ERROR_FETCHING_LIST_DATA) }));
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const triggerBulkDownloadJob = createAsyncThunk<
  void,
  BulkDownloadJobPayload
>('triggerBulkDownloadJob', async (payload, { dispatch }) => {
  try {
    const downloadListApi = `${BASE_URL}/job/?category=workflow`;

    dispatch(setError({ message: '' }));
    const response = await axios.post(downloadListApi, payload, {
      headers: getRequestHeaders(),
    });
    const { id: jobId } = response.data?.payload ?? {};
    dispatch(setCurrentBulkDownloadJobId(jobId));
    dispatch(
      renderModule({
        id: 'DDP_DETAILS',
        props: { jobId },
      }),
    );
  } catch (error) {
    dispatch(setError({ message: locale(Keys.ERROR_DOWNLOADING_LIST) }));
  }
});

export const fetchRevyreV1EntityDetails = createAsyncThunk(
  'entityDetails',
  async (
    {
      domainType,
      workflowType,
      entityId,
    }: {
      domainType: DomainType;
      workflowType?: WorkflowType;
      entityId: string;
      type?: string;
    },
    { dispatch },
  ) => {
    workflowType = workflowType ?? getCurrentWorkflowType(getState());
    try {
      const entityDetailsApi = `${url}/vymo/v3/workflow/${
        domainType !== 'workflow' ? domainType : workflowType
      }/${entityId}`;
      dispatch(setLoading({ loading: true }));
      const response = await axios.get(entityDetailsApi, {
        headers: getRequestHeaders(),
      });

      dispatch(
        setDetails({
          entityId,
          data: response?.data,
          revyre: true,
        }),
      );

      const associatedWorkflow =
        response?.data?.payload?.mutatedEntity?.workflowType;
      const associatedWorkflowId = getWorkflowCode(
        response?.data?.payload?.mutatedEntity?.workflowId,
      );

      if (associatedWorkflow) {
        dispatch(setCurrentWorkflowType(associatedWorkflow));
        dispatch(
          fetchModuleConfig({
            // @ts-ignore
            domainType: 'workflow',
          }),
        );
      }
      if (associatedWorkflowId) {
        dispatch(
          fetchRevyreV1EntityDetails({
            domainType: 'workflow',
            workflowType: associatedWorkflow,
            entityId: associatedWorkflowId,
          }),
        );
      }
    } catch (error) {
      dispatch(setError({ message: locale(Keys.ERROR_FETCHING_DETAILS_DATA) }));
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const fetchAudits = async (payload: {
  serviceType: string;
  serviceId: string;
}) =>
  axios.post(`${BASE_URL}/auditConsumer/audits`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchHistory = async (payload: {
  serviceType: string;
  serviceId: string;
}) =>
  axios.post(`${BASE_URL}/auditConsumer/history`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchEntityActivities = createAsyncThunk(
  'entityHistoryAndAudit',
  async (
    {
      serviceType,
      entityId,
    }: {
      serviceType: WorkflowType | DomainType;
      entityId: string;
    },
    { dispatch },
  ) => {
    try {
      const [auditRes, historyRes] = await Promise.all([
        fetchAudits({
          serviceType,
          serviceId: entityId,
        }),
        fetchHistory({
          serviceType,
          serviceId: entityId,
        }),
      ]);
      const payload = {
        audit: auditRes?.data?.payload ?? [],
        history: historyRes?.data?.payload ?? [],
      };

      dispatch(
        setDetailsActivitiesData({
          entityId,
          data: payload ?? {},
        }),
      );
    } catch (error) {
      dispatch(setError({ message: 'Error while fetching activities data' }));
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const formSubmit = createAsyncThunk(
  'formSubmit',
  // eslint-disable-next-line max-lines-per-function, complexity
  async (
    {
      type,
      domainType,
      workflowType,
      entityId,
      eventId,
      payload,
      isBlockingScreen,
      buttonMetadata,
    }: {
      type: string;
      domainType: DomainType;
      workflowType?: WorkflowType;
      entityId: string;
      eventId: string;
      payload;
      isBlockingScreen: Boolean | undefined;
      buttonMetadata: RevyreFormProps['buttonMetadata'];
    },
    { dispatch },
  ) => {
    try {
      let response: AxiosResponse | null = null;

      dispatch(setLoading({ loading: true }));
      dispatch(setError({ message: '' }));
      workflowType = workflowType ?? getCurrentWorkflowType(getState());

      switch (domainType) {
        case 'activity':
          if (
            type === 'detailsCreate' ||
            type === 'create' ||
            type === 'createGlobal'
          ) {
            try {
              response = await createDomain(domainType, payload);
              dispatch(
                setNotification({
                  message: locale(Keys.TRAIL_ADDED_SUCCESSFULLY),
                  id: String(Date.now()),
                  variant: 'success',
                }),
              );
              if (type === 'createGlobal') {
                closePortal?.();
              }

              if (type === 'create') {
                dispatch(
                  fetchEntityList({
                    domainType,
                    payload: { pageSize: 10, pageNumber: 1 },
                  }),
                );
              } else if (type === 'detailsCreate') {
                dispatch(
                  fetchEntityDetails({
                    entityId,
                    domainType: 'workflow',
                    workflowType,
                    revyre: true,
                  }),
                );

                // eslint-disable-next-line max-depth
                if (workflowType === CASE) {
                  dispatch(
                    fetchTrails({
                      props: { workflowType },
                      payload: {
                        filters: { workflowId: entityId },
                      },
                    }),
                  );
                }
              }
            } catch (err) {
              dispatch(
                setError({
                  message: locale(Keys.SOMETHING_WENT_WRONG),
                  id: String(Date.now()),
                }),
              );
            }
          }

          if (type === 'update') {
            response = await updateDomain(
              domainType,
              entityId,
              eventId,
              payload,
            );

            if (isBlockingScreen) {
              dispatch(removeFirstPendingActivity());
            }
          }

          if (type === 'update') {
            dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
          }

          break;

        case 'payment':
          if (
            type === 'detailsCreate' ||
            type === 'create' ||
            type === 'createGlobal'
          ) {
            response = await createDomain(domainType, payload);
          }

          if (type === 'update') {
            if (buttonMetadata?.parentAssociateWorkflowType) {
              response = await updateWorkflow(
                workflowType,
                entityId,
                eventId,
                payload,
              );
              dispatch(
                fetchEntityDetails({
                  entityId,
                  domainType: 'workflow',
                  workflowType,
                  revyre: true,
                }),
              );
            } else {
              response = await updateDomain(
                domainType,
                entityId,
                eventId,
                payload,
              );
              dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
            }
          }

          break;

        default:
          if (type === 'create') {
            response = await createWorkflow(workflowType, payload);
            dispatch(
              renderModule({
                id: 'REVYRE_DETAILS',
                props: {
                  domainType,
                  workflowType,
                  entityId: response?.data?.payload?.mutatedEntity?.id,
                },
              }),
            );
          }
          if (type === 'detailsCreate') {
            response = await createWorkflow(workflowType, payload);

            const currentEntityId = getCurrentEntityId(getState());

            dispatch(
              fetchRevyreV1EntityDetails({
                domainType,
                entityId: currentEntityId,
              }),
            );
          }
          if (type === 'createGlobal') {
            response = await createWorkflow(workflowType, payload);
            closePortal();
          }
          if (type === 'update') {
            response = await updateWorkflow(
              workflowType,
              entityId,
              eventId,
              payload,
            );
            dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
          }

          break;
      }

      dispatch(removeDialog({ id: REVYRE_FORM }));
    } catch (error: any) {
      const errorResponse =
        error?.response?.data?.description ?? error?.message;
      dispatch(removeDialog({ id: REVYRE_FORM }));
      dispatch(
        setError({
          message: errorResponse ?? locale(Keys.SOMETHING_WENT_WRONG),
        }),
      );
      // update form error
    } finally {
      dispatch(setLoading({ loading: false }));
    }
    // dispatch(removeDialog({ id: 'REVYRE_FORM' }));
  },
);

const onUpdate = async ({ dispatch, props }) => {
  const { workflowType, domainType, entityId, mode } = props;
  const filterContext = `revyre_${workflowType || domainType}_default`;
  const persistedFilters = getFiltersByContext(getState(), filterContext);
  const { pageSize } = getEntityListMetaData(getState());
  const { filters: tableParamsFilters } = getEntityListParams(getState());
  const filters =
    Object.keys(persistedFilters).length > 0
      ? persistedFilters
      : tableParamsFilters || {};
  let { currentPage } = getEntityListMetaData(getState());

  if (entityId) {
    dispatch(clearDetails());
    dispatch(setCurrentEntityId(entityId));
    dispatch(setDomainType(domainType));
    if (workflowType) {
      dispatch(setCurrentWorkflowType(workflowType));
    }

    await dispatch(fetchModuleConfig({ domainType }));
    dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
  } else {
    if (mode !== 'details') {
      dispatch(setCurrentEntityId(''));
      currentPage = 1;
      dispatch(resetEntityListData());
    }
    dispatch(setDomainType(domainType));
    if (workflowType) {
      dispatch(setCurrentWorkflowType(workflowType));
    }
    await dispatch(fetchModuleConfig({ domainType }));
    dispatch(
      fetchEntityList({
        domainType,
        payload: {
          filters,
          page: {
            pageSize,
            pageNumber: currentPage,
          },
        },
      }),
    );
  }
};

export const syncWorkflowConfigToSlice = (dispatch) => {
  const revyreWorkflow = getRevyreWorkflowConfig();
  // Setting the workflow config to store from what we have in lms
  revyreWorkflow.forEach((workflowData) => {
    const workflowType = workflowData.code;
    // TODO - hardcoded backends should send the correct data
    const domainType = 'workflow';
    const workflowConfig = getRevyreConfig(getState())?.[workflowType] ?? null;

    if (workflowConfig) {
      dispatch(
        setModuleConfigData({
          workflowType,
          domainType,
          data: {
            code: workflowType,
            name: workflowData.name,
            ...workflowConfig,
          },
        }),
      );
    }
  });
};

const onActivate = ({ dispatch }) => {
  syncWorkflowConfigToSlice(dispatch);
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate, onUpdate };
